import {
  and,
  asc,
  desc,
  eq,
  gt,
  isNotNull,
  isNull,
  like,
  lt,
  lte,
  ne,
  type PowerSyncDrizzleDB,
  sql,
  todo,
} from "@listto/powersync";
import { liveTodoFilters } from "../live/filters";
import type { UseTodosOptions } from "../types/todos";
import { toNumber } from "@fleet/utils";

const HOURS_PER_DAY = 23;
const MINUTES_PER_HOUR = 59;
const SECONDS_PER_MINUTE = 59;
const MS_PER_SECOND = 999;
const DEFAULT_LIMIT = 100;

/**
 * Shared Drizzle query builder for Todos.
 */
export function getTodosQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  options: UseTodosOptions = {},
) {
  const end = new Date();
  end.setHours(HOURS_PER_DAY, MINUTES_PER_HOUR, SECONDS_PER_MINUTE, MS_PER_SECOND);

  const filters = [eq(todo.user_id, userId), ...liveTodoFilters()];

  if (options.myDayOnly) {
    filters.push(isNotNull(todo.my_day_date), lte(todo.my_day_date, end.toISOString()));
  }
  if (options.importantOnly) {
    filters.push(eq(todo.important, 1));
  }
  if (options.plannedOnly) {
    filters.push(isNotNull(todo.due_date));
  }
  if (options.searchQuery) {
    filters.push(like(todo.text, `%${options.searchQuery}%`));
  }
  if (options.listId === null) {
    filters.push(isNull(todo.list_id));
  }
  if (typeof options.listId === "string") {
    filters.push(eq(todo.list_id, options.listId));
  }

  const query = db
    .select()
    .from(todo)
    .where(and(...filters))
    .orderBy(desc(todo.important), asc(todo.completed), desc(todo.updated_at));

  if (options.limit !== undefined) {
    return query.limit(options.limit);
  }

  return query;
}

/**
 * Shared Drizzle query builder for counting Todos.
 */
export function getTodosCountQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  options: UseTodosOptions = {},
) {
  const end = new Date();
  end.setHours(HOURS_PER_DAY, MINUTES_PER_HOUR, SECONDS_PER_MINUTE, MS_PER_SECOND);

  const filters = [eq(todo.user_id, userId), ne(todo.completed, 1), ...liveTodoFilters()];

  if (options.myDayOnly) {
    filters.push(isNotNull(todo.my_day_date), lte(todo.my_day_date, end.toISOString()));
  }
  if (options.importantOnly) {
    filters.push(eq(todo.important, 1));
  }
  if (options.plannedOnly) {
    filters.push(isNotNull(todo.due_date));
  }
  if (options.searchQuery) {
    filters.push(like(todo.text, `%${options.searchQuery}%`));
  }
  if (options.listId === null) {
    filters.push(isNull(todo.list_id));
  }
  if (typeof options.listId === "string") {
    filters.push(eq(todo.list_id, options.listId));
  }

  return db
    .select({
      count: sql<number>`count(${todo.id})`.mapWith(Number),
    })
    .from(todo)
    .where(and(...filters));
}

/**
 * Shared Drizzle query builder for counting today's todos.
 */
export function getMyDayTodosCountQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  todayEndISO?: string,
  options?: { searchQuery?: string },
) {
  let endISO = todayEndISO;
  if (!endISO) {
    const end = new Date();
    end.setHours(HOURS_PER_DAY, MINUTES_PER_HOUR, SECONDS_PER_MINUTE, MS_PER_SECOND);
    endISO = end.toISOString();
  }

  const filters = [
    eq(todo.user_id, userId),
    isNotNull(todo.my_day_date),
    lte(todo.my_day_date, endISO),
    ne(todo.completed, 1),
    ...liveTodoFilters(),
  ];

  if (options?.searchQuery) {
    filters.push(like(todo.text, `%${options.searchQuery}%`));
  }

  return db
    .select({
      count: sql<number>`count(${todo.id})`.mapWith(Number),
    })
    .from(todo)
    .where(and(...filters));
}

/**
 * Shared Drizzle query builder for a single Todo detail.
 */
export function getTodoDetailQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(todo).where(eq(todo.id, id)).limit(1);
}

export interface TodoStats {
  total: number;
  completed: number;
  important: number;
}

/**
 * Shared Drizzle query builder for Todo stats (total, completed, important).
 */
export function getTodoStatsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      total: sql<number>`count(${todo.id})`.mapWith(Number),
      completed: sql<number>`sum(case when ${todo.completed} = 1 then 1 else 0 end)`.mapWith(
        Number,
      ),
      important: sql<number>`sum(case when ${todo.important} = 1 then 1 else 0 end)`.mapWith(
        Number,
      ),
    })
    .from(todo)
    .where(and(eq(todo.user_id, userId), ...liveTodoFilters()));
}

/**
 * Shared Drizzle query builder for fetching today's todos.
 */
export function getMyDayTodosQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  todayEndISO?: string,
  options?: { limit?: number; searchQuery?: string },
) {
  let endISO = todayEndISO;
  if (!endISO) {
    const end = new Date();
    end.setHours(HOURS_PER_DAY, MINUTES_PER_HOUR, SECONDS_PER_MINUTE, MS_PER_SECOND);
    endISO = end.toISOString();
  }

  const filters = [
    eq(todo.user_id, userId),
    isNotNull(todo.my_day_date),
    lte(todo.my_day_date, endISO),
    ...liveTodoFilters(),
  ];

  if (options?.searchQuery) {
    filters.push(like(todo.text, `%${options.searchQuery}%`));
  }

  return db
    .select()
    .from(todo)
    .where(and(...filters))
    .orderBy(asc(todo.completed), desc(todo.updated_at))
    .limit(options?.limit ?? DEFAULT_LIMIT);
}

/**
 * Shared Drizzle query builder for fetching upcoming reminders.
 *
 * The trigger time is `COALESCE(reminder_at, due_date)`: an explicit reminder
 * wins, otherwise the due date acts as the reminder. This keeps the notification
 * layer aligned with the scheduling UI, where the user can set an alert time
 * distinct from the due date.
 */
export function getUpcomingRemindersQuery(
  db: PowerSyncDrizzleDB,
  options: { limit: number; maxDueDate?: string },
) {
  const triggerTime = sql`coalesce(${todo.reminder_at}, ${todo.due_date})`;
  const filters = [
    eq(todo.completed, 0),
    gt(triggerTime, new Date().toISOString()),
    ...liveTodoFilters(),
  ];

  if (options.maxDueDate) {
    filters.push(lt(triggerTime, options.maxDueDate));
  }

  return db
    .select()
    .from(todo)
    .where(and(...filters))
    .orderBy(asc(triggerTime))
    .limit(options.limit);
}

export interface SmartViewCounts {
  all_count: number;
  important_count: number;
  planned_count: number;
  inbox_count: number;
  my_day_count: number;
}

/**
 * Shared Drizzle query builder for aggregate counts across smart views.
 */
export function getSmartViewCountsQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  endOfDayISO?: string,
) {
  let endISO = endOfDayISO;
  if (!endISO) {
    const end = new Date();
    end.setHours(HOURS_PER_DAY, MINUTES_PER_HOUR, SECONDS_PER_MINUTE, MS_PER_SECOND);
    endISO = end.toISOString();
  }

  return db
    .select({
      all_count: sql<number>`count(${todo.id})`.mapWith(Number),
      important_count: sql<number>`sum(case when ${todo.important} = 1 then 1 else 0 end)`.mapWith(
        Number,
      ),
      planned_count:
        sql<number>`sum(case when ${todo.due_date} is not null then 1 else 0 end)`.mapWith(Number),
      inbox_count: sql<number>`sum(case when ${todo.list_id} is null then 1 else 0 end)`.mapWith(
        Number,
      ),
      my_day_count:
        sql<number>`sum(case when ${todo.my_day_date} is not null and ${todo.my_day_date} <= ${endISO} then 1 else 0 end)`.mapWith(
          Number,
        ),
    })
    .from(todo)
    .where(and(eq(todo.user_id, userId), ...liveTodoFilters()));
}

/**
 * Trash: individually soft-deleted tasks (tasks in a trashed list are restored
 * by restoring the list, so they do not appear here).
 */
export function getTrashedTodosQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(todo)
    .where(and(eq(todo.user_id, userId), isNotNull(todo.deleted_at)))
    .orderBy(desc(todo.deleted_at));
}

/**
 * Maps raw SmartViewCounts row (or array of rows from TanStack Query cache) to a cleaner object for the UI.
 */
export function mapSmartViewCounts(row?: SmartViewCounts | SmartViewCounts[] | null) {
  const target = Array.isArray(row) ? row[0] : row;
  return {
    all: target?.all_count ?? 0,
    important: target?.important_count ?? 0,
    planned: target?.planned_count ?? 0,
    inbox: target?.inbox_count ?? 0,
    myDay: target?.my_day_count ?? 0,
  };
}

/**
 * Maps raw aggregate query row to normalized stats object.
 */
export function mapTodoStats(row?: TodoStats | null) {
  return {
    total: toNumber(row?.total),
    completed: toNumber(row?.completed),
    important: toNumber(row?.important),
  };
}
